import { MouseEvent, ReactElement, ReactNode } from 'react';
import Image from 'next/image';

import styles from './MiniProductCard.module.scss';

export interface MiniProductCardProps {
  /** Product Detail Page URL — the card's own link target. */
  productUrl: string;
  productName: string;
  imageSrc: string;
  /** Formatted, ready-to-render price. Struck through beside `salePrice`
      when both are given. */
  displayPrice: string;
  salePrice?: string;
  /** Rendered in the trailing action slot (e.g. a Quick Add trigger).
      Deliberately outside the card's own `<a>` rather than nested inside
      it — a `<button>` inside an `<a>` is invalid HTML and behaves
      inconsistently across assistive tech/browsers — so clicking it can
      never trigger the card's own navigation, and Tab order still reaches
      the link, then this slot, in one natural pass. */
  cartAction?: ReactNode;
  priority?: boolean;
  /** Fires for the card's own link only; `cartAction` is outside it. */
  onClick?: (e: MouseEvent<HTMLAnchorElement>) => void;
}

/**
 * Mini Product Card (Figma "MEC Component Library", node 419:6774): a
 * highly compact product summary — image, name, price, and an icon-only
 * add-to-cart action — for space-constrained contexts. Lives under
 * `UI/Cards` rather than a product-feature folder because Figma itself
 * groups it under a general "Cards" section, not a product-specific one —
 * `_components/UI` is this codebase's existing home for that kind of
 * feature-agnostic shared primitive (`Chip`, `Tag`, `SelectionCell`, etc.),
 * so `Cards` sits alongside those as a new subgrouping rather than a new
 * top-level folder. Its one real caller today is `ImageHotspot`'s "shop the
 * look" popup (`ProductTile` composes this with a `QuickAdd`
 * trigger as `cartAction`), which is also the Figma component's own
 * "Related Components" callout — but nothing here is hotspot-specific.
 *
 * Two other components in this codebase are also named `MiniProductCard`
 * (`_components/ProductStrip/MiniProductCard`, `_components/MiniProductStrip/
 * MiniProductCard`) — near-duplicates of each other, used for PDP/outfit
 * product strips. Neither matches this Figma component (no cart action, and
 * their card is an `<article>` wrapping an inner `<a>` rather than the a11y
 * spec's "the card itself is the link"). They likely represent the same
 * underlying design intent and are worth consolidating at some point, but
 * that's out of scope here — left untouched.
 *
 * A11y: the link's accessible name is the full product name + price (not
 * just the name) so a screen reader announces the price without needing to
 * enter the card; `cartAction` carries its own accessible name (e.g.
 * `QuickAdd`'s "Quick add {title} to cart"). `title` gives sighted mouse
 * users the same full untruncated name on hover that the 2-line-clamped
 * `.name` visually cuts off.
 */
const MiniProductCard = ({
  productUrl,
  productName,
  imageSrc,
  displayPrice,
  salePrice,
  cartAction,
  priority = false,
  onClick,
}: MiniProductCardProps): ReactElement => (
  <article className={styles.card}>
    <a
      href={productUrl}
      className={styles.link}
      aria-label={`${productName}, ${salePrice || displayPrice}`}
      title={productName}
      onClick={onClick}
    >
      <div className={styles.imageWrapper}>
        <Image
          src={imageSrc}
          alt=""
          fill
          sizes="(min-width: 988px) 104px, 80px"
          className={styles.image}
          priority={priority}
          loading={priority ? undefined : 'eager'}
        />
      </div>
      <div className={styles.info}>
        <p className={styles.name}>{productName}</p>
        <div className={styles.price}>
          {salePrice ? (
            <>
              <span className={styles.salePrice}>{salePrice}</span>
              <span className={styles.originalPrice}>{displayPrice}</span>
            </>
          ) : (
            <span>{displayPrice}</span>
          )}
        </div>
      </div>
    </a>
    {cartAction && <div className={styles.cartAction}>{cartAction}</div>}
  </article>
);

export default MiniProductCard;
